import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronDown, Search } from "lucide-react";
import { api } from "@/utils/api-client";
import { LoadingState } from "@/components/LoadingState";
import { PageHero } from "../_components/page-hero";
import { FAQS } from "../_data/faq";

type LegalListItem = { title: string; slug: string; metaDescription?: string; updatedAt?: string };
type LegalPageData = { title: string; slug: string; content: string; metaDescription?: string; updatedAt?: string };

const FAQ_TAB = "faq";

/* ── Minimal, safe markdown → HTML for legal content ───────────────────── */
function renderMarkdown(md: string): string {
  const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  let html = esc(md)
    .replace(/^#### (.+)$/gm, '<h4 class="mt-6 mb-2 font-display text-base font-semibold text-foreground">$1</h4>')
    .replace(/^### (.+)$/gm, '<h3 class="mt-8 mb-3 font-display text-lg font-semibold text-foreground">$1</h3>')
    .replace(/^## (.+)$/gm, '<h2 class="mt-10 mb-3 border-b border-border pb-2 font-display text-xl font-semibold text-foreground">$1</h2>')
    .replace(/^# (.+)$/gm, '<h1 class="mt-10 mb-4 font-display text-2xl font-semibold text-foreground">$1</h1>')
    .replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-foreground">$1</strong>')
    .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" class="text-primary underline hover:opacity-80" target="_blank" rel="noopener noreferrer">$1</a>')
    .replace(/^- (.+)$/gm, '<li class="ml-4 leading-7 text-muted-foreground">$1</li>')
    .replace(/^---$/gm, '<hr class="my-6 border-border" />')
    .replace(/\n\n/g, '</p><p class="mb-3 leading-7 text-muted-foreground">')
    .replace(/\n/g, "<br />");
  html = html.replace(/(<li[^>]*>.*?<\/li>\s*)+/g, (m) => `<ul class="mb-3 ml-2 list-disc space-y-1">${m}</ul>`);
  return `<p class="mb-3 leading-7 text-muted-foreground">${html}</p>`;
}

export default function LegalPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [pages, setPages] = useState<LegalListItem[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  // Load the list of published legal pages for the tabs.
  useEffect(() => {
    setListLoading(true);
    setListError(null);
    api.get<LegalListItem[]>("/api/public/legal")
      .then((data) => setPages(data || []))
      .catch(() => setListError("Failed to load legal pages"))
      .finally(() => setListLoading(false));
  }, []);

  // All tabs = legal slugs + the FAQ tab.
  const tabs = useMemo(
    () => [...pages.map((p) => ({ key: p.slug, label: p.title })), { key: FAQ_TAB, label: "FAQ" }],
    [pages],
  );

  const requested = searchParams.get("tab");
  const activeKey = useMemo(() => {
    if (requested && tabs.some((t) => t.key === requested)) return requested;
    return tabs[0]?.key ?? FAQ_TAB;
  }, [requested, tabs]);

  const setActive = (key: string) => setSearchParams({ tab: key }, { replace: true });

  return (
    <>
      <PageHero
        eyebrow="Legal & help"
        title={<>Policies and <span className="gradient-text">answers.</span></>}
        sub="Legal documents and frequently asked questions in one place. Pick a tab to read."
      />

      <section className="pb-24">
        <div className="mx-auto max-w-4xl px-4 sm:px-6">
          <LoadingState loading={listLoading} error={listError} onRetry={() => window.location.reload()} loadingMessage="Loading…">
            {/* Tabs */}
            <div className="flex flex-wrap gap-2 border-b border-border pb-4">
              {tabs.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setActive(t.key)}
                  className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${
                    t.key === activeKey
                      ? "border-primary/50 bg-primary/10 text-foreground"
                      : "border-border text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <div className="mt-10">
              {activeKey === FAQ_TAB ? <FaqTab /> : <LegalTab slug={activeKey} />}
            </div>
          </LoadingState>
        </div>
      </section>
    </>
  );
}

/* ── Legal document tab (lazy-loads its content) ───────────────────────── */
function LegalTab({ slug }: { slug: string }) {
  const [page, setPage] = useState<LegalPageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    setPage(null);
    api.get<LegalPageData>(`/api/public/legal/${slug}`)
      .then((data) => { if (active) setPage(data); })
      .catch(() => { if (active) setError("Failed to load this page"); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [slug]);

  return (
    <LoadingState loading={loading} error={error} onRetry={() => window.location.reload()} variant="inline">
      {page && (
        <article>
          <h2 className="font-display text-2xl font-semibold tracking-tight text-foreground">{page.title}</h2>
          {page.updatedAt && (
            <p className="mt-2 text-xs text-muted-foreground">
              Updated {new Date(page.updatedAt).toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" })}
            </p>
          )}
          <div className="mt-8" dangerouslySetInnerHTML={{ __html: renderMarkdown(page.content) }} />
        </article>
      )}
    </LoadingState>
  );
}

/* ── FAQ tab (searchable accordion from static data) ───────────────────── */
function FaqTab() {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState<string | null>(null);
  const categories = useMemo(() => Array.from(new Set(FAQS.map((f) => f.category))), []);
  const filtered = useMemo(() => {
    if (!q.trim()) return FAQS;
    const s = q.toLowerCase();
    return FAQS.filter((f) => f.q.toLowerCase().includes(s) || f.a.toLowerCase().includes(s));
  }, [q]);

  return (
    <div>
      <label className="flex max-w-md items-center gap-2 rounded-xl border border-border bg-surface/60 px-4 py-2.5 text-sm">
        <Search className="h-4 w-4 text-muted-foreground" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search the FAQ…"
          className="flex-1 bg-transparent outline-none placeholder:text-muted-foreground"
        />
      </label>

      {categories.map((cat) => {
        const items = filtered.filter((f) => f.category === cat);
        if (!items.length) return null;
        return (
          <div key={cat} className="mt-10 first:mt-8">
            <h3 className="font-display text-sm font-medium uppercase tracking-[0.18em] text-primary">{cat}</h3>
            <div className="mt-4 divide-y divide-border border-y border-border">
              {items.map((f) => {
                const id = `${cat}-${f.q}`;
                const isOpen = open === id;
                return (
                  <div key={id}>
                    <button
                      type="button"
                      onClick={() => setOpen(isOpen ? null : id)}
                      className="flex w-full items-start justify-between gap-4 py-5 text-left"
                    >
                      <span className="font-display text-base font-medium">{f.q}</span>
                      <ChevronDown className={`h-5 w-5 flex-shrink-0 text-muted-foreground transition-transform ${isOpen ? "rotate-180 text-primary" : ""}`} />
                    </button>
                    {isOpen && <p className="pb-6 pr-8 text-sm leading-relaxed text-muted-foreground">{f.a}</p>}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {!filtered.length && <p className="mt-12 text-center text-sm text-muted-foreground">No matches for &ldquo;{q}&rdquo;.</p>}
    </div>
  );
}
